import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type ChannelTagInsert = Database['public']['Tables']['channel_tags']['Insert'];
type ChannelTagUpdate = Database['public']['Tables']['channel_tags']['Update'];

export interface CreateTagInput {
  name: string;
  description?: string;
  providerId: number;
  versionId: number;
  regionId: number;
}

export interface UpdateTagInput {
  id: number;
  name?: string;
  description?: string;
}

export interface AssignTagsInput {
  channelIds: number[];
  tagIds: number[];
  operation: 'add' | 'remove' | 'replace';
}

// Create a new tag
export async function createTag(input: CreateTagInput) {
  const tagData: ChannelTagInsert = {
    name: input.name.trim(),
    description: input.description?.trim() || null,
    provider_id: input.providerId,
    version_id: input.versionId,
    region_id: input.regionId
  };

  const { data, error } = await supabase
    .from('channel_tags')
    .insert(tagData)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Update an existing tag
export async function updateTag(input: UpdateTagInput) {
  const updateData: ChannelTagUpdate = {
    updated_at: new Date().toISOString()
  };

  if (input.name !== undefined) {
    updateData.name = input.name.trim();
  }
  if (input.description !== undefined) {
    updateData.description = input.description?.trim() || null;
  }

  const { data, error } = await supabase
    .from('channel_tags')
    .update(updateData)
    .eq('id', input.id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// Delete a tag (will cascade delete assignments)
export async function deleteTag(tagId: number) {
  const { error } = await supabase
    .from('channel_tags')
    .delete()
    .eq('id', tagId);

  if (error) throw error;
}

// Assign tags to channels
export async function assignTagsToChannels(input: AssignTagsInput) {
  const { channelIds, tagIds, operation } = input;

  if (channelIds.length === 0 || tagIds.length === 0) return;

  if (operation === 'remove' || operation === 'replace') {
    // Remove existing assignments
    const { error: deleteError } = await supabase
      .from('channel_tag_assignments')
      .delete()
      .in('channel_id', channelIds)
      .in('tag_id', operation === 'remove' ? tagIds : []);

    if (deleteError) throw deleteError;
  }

  if (operation === 'add' || operation === 'replace') {
    // Create new assignments
    const assignments = channelIds.flatMap(channelId =>
      tagIds.map(tagId => ({
        channel_id: channelId,
        tag_id: tagId
      }))
    );

    const { error: insertError } = await supabase
      .from('channel_tag_assignments')
      .upsert(assignments, {
        onConflict: 'channel_id,tag_id',
        ignoreDuplicates: true
      });

    if (insertError) throw insertError;
  }
}

// Remove all tag assignments for specific channels
export async function removeAllTagsFromChannels(channelIds: number[]) {
  if (channelIds.length === 0) return;

  const { error } = await supabase
    .from('channel_tag_assignments')
    .delete()
    .in('channel_id', channelIds);

  if (error) throw error;
}